import React, { useState } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Autocomplete, CircularProgress, IconButton, Box } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { customersAPI, vendorsAPI, inventoryAPI, departmentsAPI, warehouseAPI, commissionAgentsAPI, crmAPI, hrAPI, brandsAPI, usersAPI, itemCategoriesAPI, numberingAPI, expenseCategoriesAPI, chartOfAccountsAPI, fixedAssetsAPI, projectsAPI } from '../../services/api';
import { invalidateInventory } from '../../utils/inventoryCache';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import PhoneInput from './PhoneInput';
import { EditorDialog, FormSection } from './UnifiedUI';
import FormField from './FormField';
import { useFormDraft } from '../../hooks/useFormDraft';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

import { detectUserLocation, getPhonePrefix } from '../../utils/locationUtils';

export const QuickAddCustomer = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isQuickCreateEnabled = useFeatureFlag('quickCreate', false);
  const [saving, setSaving] = useState(false);
  const { hasDraft, saveDraft, loadDraft, clearDraft } = useFormDraft('quickAddCustomer');
  const empty = { code: '', name: '', email: '', countryCode: '+44', phone: '', type: 'business', taxId: '', paymentTerms: 30, creditLimit: 0, address: '', contactPersonName: '', contactPersonEmail: '', contactPersonCountryCode: '+44', contactPersonPhone: '' };

  const [formData, setFormData] = useState(empty);
  const set = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (isQuickCreateEnabled) saveDraft(next);
      return next;
    });
  };

  React.useEffect(() => {
    if (!open) return;
    const init = async () => {
      try {
        const [locRes, codeRes] = await Promise.all([
          detectUserLocation(),
          numberingAPI.peek('customer')
        ]);
        const prefix = getPhonePrefix(locRes.countryCode);
        setFormData(prev => ({
          ...prev,
          countryCode: prefix,
          contactPersonCountryCode: prefix,
          code: codeRes.data?.nextNumber || ''
        }));

        if (isQuickCreateEnabled && hasDraft) {
          const draft = loadDraft();
          if (draft) {
            setFormData(prev => ({ ...prev, ...draft }));
            enqueueSnackbar('Restored unsaved draft', { variant: 'info' });
          }
        }
      } catch { /* silent */ }
    };
    init();
  }, [open, isQuickCreateEnabled, hasDraft, loadDraft]); // Add deps

  const handleSave = async (keepOpen = false) => {
    if (!formData.name) { enqueueSnackbar('Name is required', { variant: 'error' }); return; }
    setSaving(true);
    try {
      const saveData = {
        ...formData,
        phone: formData.countryCode && formData.phone ? `${formData.countryCode} ${formData.phone}` : formData.phone,
        contactPersonPhone: formData.contactPersonCountryCode && formData.contactPersonPhone ? `${formData.contactPersonCountryCode} ${formData.contactPersonPhone}` : formData.contactPersonPhone,
        contactPersonEmail: formData.contactPersonEmail?.trim() || null
      };
      const response = await customersAPI.create(saveData);
      enqueueSnackbar('Customer created successfully', { variant: 'success' });
      // Unwrap the response: backend returns { success, status, customer: {...} }
      // Pass just the customer object so consumers can read .name, .id, etc. directly
      onSuccess(response.data?.customer || response.data);

      if (isQuickCreateEnabled) clearDraft();

      if (keepOpen && isQuickCreateEnabled) {
        setFormData(empty);
        // Refresh auto-generated code
        numberingAPI.peek('customer').then(codeRes => {
          setFormData(prev => ({ ...prev, code: codeRes.data?.nextNumber || '' }));
        }).catch(() => { });
      } else {
        onClose();
        setFormData(empty);
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create customer', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const isNotIndividual = formData.type !== 'individual';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Customer</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Customer Code" value={formData.code} InputProps={{ readOnly: true }} helperText="Auto-generated" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label={formData.type === 'business' ? 'Business Name' : formData.type === 'government' ? 'Organization Name' : 'Name'} value={formData.name} onChange={(e) => set('name', e.target.value)} required />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Email" type="email" value={formData.email} onChange={(e) => set('email', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneInput label="Phone Number" countryCode={formData.countryCode} phoneNumber={formData.phone} onCountryCodeChange={(v) => set('countryCode', v)} onPhoneNumberChange={(v) => set('phone', v)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={formData.type} onChange={(e) => set('type', e.target.value)} label="Type">
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="government">Government</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Tax ID" value={formData.taxId} onChange={(e) => set('taxId', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Payment Terms (Days)" type="number" value={formData.paymentTerms} onChange={(e) => set('paymentTerms', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Credit Limit" type="number" value={formData.creditLimit} onChange={(e) => set('creditLimit', e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" multiline rows={2} value={formData.address} onChange={(e) => set('address', e.target.value)} />
          </Grid>
          {isNotIndividual && (
            <>
              <Grid item xs={12}>
                <TextField fullWidth label="Contact Person Name" value={formData.contactPersonName} onChange={(e) => set('contactPersonName', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Contact Person Email" type="email" value={formData.contactPersonEmail} onChange={(e) => set('contactPersonEmail', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneInput label="Contact Person Phone" countryCode={formData.contactPersonCountryCode} phoneNumber={formData.contactPersonPhone} onCountryCodeChange={(v) => set('contactPersonCountryCode', v)} onPhoneNumberChange={(v) => set('contactPersonPhone', v)} />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {isQuickCreateEnabled && (
          <Button onClick={() => handleSave(true)} variant="outlined" disabled={saving}>
            {saving ? 'Creating...' : 'Save & New'}
          </Button>
        )}
        <Button onClick={() => handleSave(false)} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddVendor = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isQuickCreateEnabled = useFeatureFlag('quickCreate', false);
  const [saving, setSaving] = useState(false);
  const { hasDraft, saveDraft, loadDraft, clearDraft } = useFormDraft('quickAddVendor');
  const empty = { code: '', name: '', email: '', countryCode: '+44', phone: '', type: 'business', taxId: '', paymentTerms: 30, creditLimit: 0, address: '' };

  const [formData, setFormData] = useState(empty);
  const set = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (isQuickCreateEnabled) saveDraft(next);
      return next;
    });
  };

  React.useEffect(() => {
    if (!open) return;
    const init = async () => {
      try {
        const [locRes, codeRes] = await Promise.all([
          detectUserLocation(),
          numberingAPI.peek('vendor')
        ]);
        const prefix = getPhonePrefix(locRes.countryCode);
        setFormData(prev => ({ ...prev, countryCode: prefix, code: codeRes.data?.nextNumber || '' }));

        if (isQuickCreateEnabled && hasDraft) {
          const draft = loadDraft();
          if (draft) {
            setFormData(prev => ({ ...prev, ...draft }));
            enqueueSnackbar('Restored unsaved draft', { variant: 'info' });
          }
        }
      } catch { /* silent */ }
    };
    init();
  }, [open, isQuickCreateEnabled, hasDraft, loadDraft]);

  const handleSave = async (keepOpen = false) => {
    if (!formData.name) { enqueueSnackbar('Name is required', { variant: 'error' }); return; }
    setSaving(true);
    try {
      const saveData = {
        ...formData,
        phone: formData.countryCode && formData.phone ? `${formData.countryCode} ${formData.phone}` : formData.phone
      };
      const response = await vendorsAPI.create(saveData);
      enqueueSnackbar('Vendor created successfully', { variant: 'success' });
      onSuccess(response.data);

      if (isQuickCreateEnabled) clearDraft();

      if (keepOpen && isQuickCreateEnabled) {
        setFormData(empty);
        // Refresh auto-generated code
        numberingAPI.peek('vendor').then(codeRes => {
          setFormData(prev => ({ ...prev, code: codeRes.data?.nextNumber || '' }));
        }).catch(() => { });
      } else {
        onClose();
        setFormData(empty);
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create vendor', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Vendor</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Vendor Code" value={formData.code} InputProps={{ readOnly: true }} helperText="Auto-generated" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label={formData.type === 'business' ? 'Business Name' : formData.type === 'government' ? 'Organization Name' : 'Name'} value={formData.name} onChange={(e) => set('name', e.target.value)} required />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Email" type="email" value={formData.email} onChange={(e) => set('email', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneInput label="Phone Number" countryCode={formData.countryCode} phoneNumber={formData.phone} onCountryCodeChange={(v) => set('countryCode', v)} onPhoneNumberChange={(v) => set('phone', v)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={formData.type} onChange={(e) => set('type', e.target.value)} label="Type">
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="government">Government</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Tax ID" value={formData.taxId} onChange={(e) => set('taxId', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Payment Terms (Days)" type="number" value={formData.paymentTerms} onChange={(e) => set('paymentTerms', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Credit Limit" type="number" value={formData.creditLimit} onChange={(e) => set('creditLimit', e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" multiline rows={2} value={formData.address} onChange={(e) => set('address', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {isQuickCreateEnabled && (
          <Button onClick={() => handleSave(true)} variant="outlined" disabled={saving}>
            {saving ? 'Creating...' : 'Save & New'}
          </Button>
        )}
        <Button onClick={() => handleSave(false)} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Vendor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddInventoryItem = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const companyId = user?.company?.id;
  const tenantId = user?.tenantId;
  const [saving, setSaving] = useState(false);
  const [brands, setBrands] = useState([]);
  const [formData, setFormData] = useState({ name: '', sku: '', sellingPrice: '', costPrice: '', unit: 'pcs', brandId: '' });

  React.useEffect(() => {
    if (open) {
      brandsAPI.getAll().then(res => {
        const data = res.data?.brands || res.data?.data?.brands || res.data?.data || [];
        setBrands(Array.isArray(data) ? data : []);
      }).catch(err => console.error(err));
    }
  }, [open]);

  const handleSave = async () => {
    if (!formData.name || !formData.sku) {
      enqueueSnackbar('Name and SKU are required', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, brandId: formData.brandId || null };
      const response = await inventoryAPI.create(payload);
      enqueueSnackbar('Item created successfully', { variant: 'success' });
      // Invalidate shared cache so all PO/Invoice/Quotation forms see this item immediately
      invalidateInventory(queryClient, { tenantId, companyId });
      onSuccess(response.data);
      onClose();
      setFormData({ name: '', sku: '', sellingPrice: '', costPrice: '', unit: 'pcs', brandId: '' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create item', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Item</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TextField fullWidth label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="SKU" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} required />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Selling Price" type="number" value={formData.sellingPrice} onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Cost Price" type="number" value={formData.costPrice} onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Brand (Optional)</InputLabel>
              <Select value={formData.brandId} onChange={(e) => setFormData({ ...formData, brandId: e.target.value })} label="Brand (Optional)" displayEmpty>
                <MenuItem value=""><em>None</em></MenuItem>
                {brands.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddDepartment = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({ code: '', name: '', description: '', managerId: '' });
  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  React.useEffect(() => {
    if (!open) return;
    const init = async () => {
      try {
        const [codeRes, usersRes] = await Promise.all([
          departmentsAPI.peek(),
          usersAPI.getAll()
        ]);
        setFormData(prev => ({ ...prev, code: codeRes.data?.nextNumber || '' }));
        const uData = usersRes.data?.users || usersRes.data?.data?.users || usersRes.data?.data || [];
        setUsers(Array.isArray(uData) ? uData : []);
      } catch { /* silent */ }
    };
    init();
  }, [open]);

  const handleSave = async () => {
    if (!formData.name) { enqueueSnackbar('Name is required', { variant: 'error' }); return; }
    setSaving(true);
    try {
      const response = await departmentsAPI.create({ ...formData, managerId: formData.managerId || null });
      enqueueSnackbar('Department created successfully', { variant: 'success' });
      onSuccess(response.data);
      onClose();
      setFormData({ code: '', name: '', description: '', managerId: '' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create department', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Department</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Code" value={formData.code} InputProps={{ readOnly: true }} helperText="Auto-generated" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Name" value={formData.name} onChange={(e) => set('name', e.target.value)} required />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Manager</InputLabel>
              <Select value={formData.managerId} onChange={(e) => set('managerId', e.target.value)} label="Manager">
                <MenuItem value="">No Manager</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" multiline rows={2} value={formData.description} onChange={(e) => set('description', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Department'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddLeaveRequest = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [employeeError, setEmployeeError] = useState(null);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employeeId: '',
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    totalDays: '',
    reason: ''
  });

  const calcDays = (start, end) => {
    if (!start || !end) return '';
    const diff = Math.round((new Date(end) - new Date(start)) / 86400000) + 1;
    return diff > 0 ? diff : '';
  };

  const calcEndDate = (start, days) => {
    if (!start || !days || days < 1) return '';
    const d = new Date(start);
    d.setDate(d.getDate() + parseInt(days) - 1);
    return d.toISOString().split('T')[0];
  };

  const handleStartDateChange = (v) => {
    const days = formData.endDate ? calcDays(v, formData.endDate) : formData.totalDays;
    setFormData(prev => ({ ...prev, startDate: v, totalDays: days }));
  };

  const handleEndDateChange = (v) => {
    setFormData(prev => ({ ...prev, endDate: v, totalDays: calcDays(prev.startDate, v) }));
  };

  const handleTotalDaysChange = (v) => {
    const endDate = calcEndDate(formData.startDate, v);
    setFormData(prev => ({ ...prev, totalDays: v, endDate }));
  };

  const { hasDraft, saveDraft, loadDraft, clearDraft } = useFormDraft('quick-add-leave-request');

  React.useEffect(() => {
    if (open) {
      fetchTypes();
      fetchEmployees();
    }
  }, [open]);

  const fetchEmployees = async () => {
    try {
      const res = await hrAPI.getEmployeesList();
      setEmployees(res.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Prompt to restore draft when opening Add form
  React.useEffect(() => {
    if (open && hasDraft) {
      if (window.confirm('You have an unsaved draft. Would you like to restore it?')) {
        const draft = loadDraft();
        if (draft) setFormData(draft);
      } else {
        clearDraft();
      }
    }
  }, [open, hasDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveDraft = async () => {
    if (!formData.leaveTypeId || !formData.startDate || !formData.endDate) {
      saveDraft(formData);
      enqueueSnackbar('Draft saved locally', { variant: 'success' });
      onClose();
      return;
    }
    setSaving(true);
    setEmployeeError(null);
    try {
      await hrAPI.createLeaveRequest({ ...formData, status: 'draft' });
      clearDraft();
      enqueueSnackbar('Draft saved successfully', { variant: 'success' });
      onClose();
      setFormData({ employeeId: '', leaveTypeId: '', startDate: '', endDate: '', totalDays: '', reason: '' });
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to save draft';
      if (error.response?.status === 422) {
        setEmployeeError(msg);
      } else {
        saveDraft(formData);
        enqueueSnackbar('Saved draft locally (server save failed)', { variant: 'warning' });
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const fetchTypes = async () => {
    try {
      const res = await hrAPI.getLeaveTypes();
      setTypes(res.data?.data || []);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const handleSave = async () => {
    if (!formData.leaveTypeId || !formData.startDate || !formData.endDate) {
      enqueueSnackbar('Please fill all required fields', { variant: 'error' });
      return;
    }
    setSaving(true);
    setEmployeeError(null);
    try {
      const response = await hrAPI.createLeaveRequest(formData);
      enqueueSnackbar('Leave request submitted', { variant: 'success' });
      clearDraft();
      onSuccess(response.data);
      onClose();
      setFormData({ employeeId: '', leaveTypeId: '', startDate: '', endDate: '', totalDays: '', reason: '' });
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to submit request';
      // TC-054: show a persistent in-dialog banner for employee-not-linked 422
      if (error.response?.status === 422) {
        setEmployeeError(msg);
      } else {
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditorDialog
      open={open}
      onClose={() => { setEmployeeError(null); onClose(); }}
      title="Apply for Leave"
      onSave={handleSave}
      onSaveDraft={handleSaveDraft}
      saving={saving}
      maxWidth="sm"
    >
      {employeeError && (
        <Alert
          severity="warning"
          onClose={() => setEmployeeError(null)}
          sx={{ mb: 2, mt: 1 }}
        >
          {employeeError}
        </Alert>
      )}
      <FormSection title="Leave Details">
        <Grid item xs={12}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Employee (leave blank for self)</InputLabel>
            <Select
              value={formData.employeeId}
              onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
              label="Employee (leave blank for self)"
            >
              <MenuItem value=""><em>Self</em></MenuItem>
              {employees.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.firstName} {e.lastName} ({e.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormField
            type="select"
            label="Leave Type"
            name="leaveTypeId"
            value={formData.leaveTypeId}
            onChange={(n, v) => setFormData({ ...formData, leaveTypeId: v })}
            required
            options={types.map(type => ({ value: type.id, label: type.name }))}
          />
        </Grid>
        <Grid item xs={6}>
          <FormField
            type="date"
            label="Start Date"
            name="startDate"
            value={formData.startDate}
            onChange={(n, v) => handleStartDateChange(v)}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <FormField
            type="date"
            label="End Date"
            name="endDate"
            value={formData.endDate}
            onChange={(n, v) => handleEndDateChange(v)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            label="Total Days"
            name="totalDays"
            type="number"
            value={formData.totalDays}
            onChange={(n, v) => handleTotalDaysChange(v)}
            helperText={formData.startDate ? 'Edit to auto-update end date' : 'Set start date first'}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            label="Reason"
            name="reason"
            multiline
            rows={3}
            value={formData.reason}
            onChange={(n, v) => setFormData({ ...formData, reason: v })}
          />
        </Grid>
      </FormSection>
    </EditorDialog>
  );
};

export const QuickAddOpportunity = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [targetType, setTargetType] = useState('customer'); // 'customer' | 'lead'
  const [innerModalOpen, setInnerModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    customerId: '',
    leadId: '',
    expectedValue: '',
    probability: 50,
    stage: 'prospecting',
    expectedCloseDate: '',
    currencyCode: 'GBP',
    notes: '',
    scope: '',
    ownerId: ''
  });

  React.useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [customersRes, usersRes, leadsRes] = await Promise.all([
        customersAPI.getAll(),
        usersAPI.getAll(),
        crmAPI.getLeads().catch(() => ({ data: { data: { leads: [] } } }))
      ]);
      const cData = customersRes.data?.customers || customersRes.data?.data?.customers || customersRes.data?.data || [];
      const uData = usersRes.data?.users || usersRes.data?.data?.users || usersRes.data?.data || [];
      const lData = leadsRes.data?.data?.leads || leadsRes.data?.data || leadsRes.data?.leads || [];
      setCustomers(Array.isArray(cData) ? cData : []);
      setUsers(Array.isArray(uData) ? uData : []);
      setLeads(Array.isArray(lData) ? lData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Failed to load form data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const hasTarget = targetType === 'customer' ? !!formData.customerId : !!formData.leadId;
    if (!formData.title || !hasTarget) {
      enqueueSnackbar(`Title and ${targetType === 'customer' ? 'Customer' : 'Lead'} are required`, { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const savePayload = {
        ...formData,
        customerId: targetType === 'customer' ? formData.customerId : null,
        leadId: targetType === 'lead' ? formData.leadId : null,
        ownerId: formData.ownerId || null
      };
      const response = await crmAPI.createOpportunity(savePayload);
      enqueueSnackbar('Opportunity created', { variant: 'success' });
      onSuccess(response.data);
      onClose();
      setFormData({ title: '', customerId: '', leadId: '', expectedValue: '', probability: 50, stage: 'prospecting', expectedCloseDate: '', currencyCode: 'GBP', notes: '', scope: '', ownerId: '' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create opportunity', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Opportunity</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant={targetType === 'customer' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setTargetType('customer')}
              >
                Existing Customer
              </Button>
              <Button
                variant={targetType === 'lead' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setTargetType('lead')}
              >
                Prospective Lead
              </Button>
            </Box>
            {targetType === 'customer' ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.name || ''}
                    loading={loading}
                    value={customers.find(c => c.id === formData.customerId) || null}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, customerId: newValue ? newValue.id : '' });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
                <IconButton
                  color="primary"
                  sx={{ mt: 1 }}
                  onClick={() => setInnerModalOpen(true)}
                  title="Add New Customer"
                >
                  <AddCircleOutline />
                </IconButton>
              </Box>
            ) : (
              <Autocomplete
                options={leads}
                getOptionLabel={(option) => option.fullName ? `${option.fullName} (${option.companyName || option.email || 'Lead'})` : (option.companyName || option.email || '')}
                loading={loading}
                value={leads.find(l => l.id === formData.leadId) || null}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, leadId: newValue ? newValue.id : '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lead"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Expected Value"
              type="number"
              value={formData.expectedValue}
              onChange={(e) => setFormData({ ...formData, expectedValue: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Probability (%)"
              type="number"
              value={formData.probability}
              onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Expected Close Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.expectedCloseDate}
              onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="scope-label">Scope</InputLabel>
              <Select
                labelId="scope-label"
                value={formData.scope}
                label="Scope"
                onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="government">Government</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.name || `${option.firstName} ${option.lastName}`}
              loading={loading}
              value={users.find(u => u.id === formData.ownerId) || null}
              onChange={(event, newValue) => {
                setFormData({ ...formData, ownerId: newValue ? newValue.id : '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Owner (Optional)"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes / Description"
              multiline
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Opportunity'}
        </Button>
      </DialogActions>

      <QuickAddCustomer
        open={innerModalOpen}
        onClose={() => setInnerModalOpen(false)}
        onSuccess={(newCustomer) => {
          setCustomers(prev => [...prev, newCustomer]);
          setFormData(prev => ({ ...prev, customerId: newCustomer.id }));
        }}
      />
    </Dialog>
  );
};

export const QuickAddBatch = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [innerModalOpen, setInnerModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    batchNumber: '',
    inventoryItemId: '',
    vendorId: '',
    receivedQty: '',
    unitCost: '',
    expiryDate: '',
    manufacturingDate: ''
  });

  const { hasDraft, saveDraft, loadDraft, clearDraft } = useFormDraft('quick-add-batch');

  React.useEffect(() => {
    if (open) {
      fetchItemsAndVendors();
    }
  }, [open]);

  // Prompt to restore draft when opening Add form
  React.useEffect(() => {
    if (open && hasDraft) {
      if (window.confirm('You have an unsaved draft. Would you like to restore it?')) {
        const draft = loadDraft();
        if (draft) setFormData(draft);
      } else {
        clearDraft();
      }
    }
  }, [open, hasDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveDraft = () => {
    saveDraft(formData);
    enqueueSnackbar('Draft saved locally', { variant: 'success' });
    onClose();
  };

  const fetchItemsAndVendors = async () => {
    setLoading(true);
    try {
      const [itemsRes, vendorsRes] = await Promise.all([
        inventoryAPI.getAll({ all: true }),
        vendorsAPI.getAll({ all: true })
      ]);
      setItems(itemsRes.data?.data?.items || []);
      setVendors(vendorsRes.data?.vendors || vendorsRes.data?.data?.vendors || vendorsRes.data?.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Failed to load items or vendors', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.batchNumber || !formData.inventoryItemId) {
      enqueueSnackbar('Batch Number and Item are required', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const response = await inventoryAPI.createBatch(formData);
      enqueueSnackbar('Batch created', { variant: 'success' });
      clearDraft();
      onSuccess(response.data);
      onClose();
      setFormData({ batchNumber: '', inventoryItemId: '', receivedQty: '', unitCost: '', expiryDate: '', manufacturingDate: '' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create batch', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditorDialog
      open={open}
      onClose={onClose}
      title="Add New Batch"
      onSave={handleSave}
      onSaveDraft={handleSaveDraft}
      saving={saving}
      maxWidth="sm"
    >
      <FormSection title="Batch Details">
        <Grid item xs={12}>
          <FormField
            label="Batch Number"
            name="batchNumber"
            value={formData.batchNumber}
            onChange={(n, v) => setFormData({ ...formData, batchNumber: v })}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <FormField
                type="autocomplete"
                label="Item"
                name="inventoryItemId"
                value={formData.inventoryItemId}
                onChange={(n, v) => setFormData({ ...formData, inventoryItemId: v })}
                options={items.map(i => ({ value: i.id, label: i.name ? `${i.name} (${i.sku})` : '' }))}
                required
              />
            </Box>
            <IconButton
              color="primary"
              sx={{ mt: 1 }}
              onClick={() => setInnerModalOpen(true)}
              title="Add New Item"
            >
              <AddCircleOutline />
            </IconButton>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <FormField
            type="autocomplete"
            label="Vendor (Optional)"
            name="vendorId"
            value={formData.vendorId}
            onChange={(n, v) => setFormData({ ...formData, vendorId: v })}
            options={vendors.map(v => ({ value: v.id, label: v.name }))}
          />
        </Grid>
        <Grid item xs={6}>
          <FormField
            label="Received Quantity"
            name="receivedQty"
            type="number"
            value={formData.receivedQty}
            onChange={(n, v) => setFormData({ ...formData, receivedQty: v })}
          />
        </Grid>
        <Grid item xs={6}>
          <FormField
            label="Unit Cost"
            name="unitCost"
            type="number"
            value={formData.unitCost}
            onChange={(n, v) => setFormData({ ...formData, unitCost: v })}
          />
        </Grid>
        <Grid item xs={6}>
          <FormField
            label="Manufacturing Date"
            name="manufacturingDate"
            type="date"
            value={formData.manufacturingDate}
            onChange={(n, v) => setFormData({ ...formData, manufacturingDate: v })}
          />
        </Grid>
        <Grid item xs={6}>
          <FormField
            label="Expiry Date"
            name="expiryDate"
            type="date"
            value={formData.expiryDate}
            onChange={(n, v) => setFormData({ ...formData, expiryDate: v })}
          />
        </Grid>
      </FormSection>

      <QuickAddInventoryItem
        open={innerModalOpen}
        onClose={() => setInnerModalOpen(false)}
        onSuccess={(newItem) => {
          setItems(prev => [...prev, newItem]);
          setFormData(prev => ({ ...prev, inventoryItemId: newItem.id }));
        }}
      />
    </EditorDialog>
  );
};

export const QuickAddBrand = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', code: '', description: '', website: '' });
  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  React.useEffect(() => {
    if (!open) return;
    brandsAPI.peek().then(res => {
      setFormData(prev => ({ ...prev, code: res.data?.nextNumber || '' }));
    }).catch(() => { });
  }, [open]);

  const handleSave = async () => {
    if (!formData.name) { enqueueSnackbar('Name is required', { variant: 'error' }); return; }
    setSaving(true);
    try {
      const response = await brandsAPI.create(formData);
      enqueueSnackbar('Brand created successfully', { variant: 'success' });
      onSuccess(response.data?.data || response.data);
      onClose();
      setFormData({ name: '', code: '', description: '', website: '' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create brand', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Brand</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Brand Code" value={formData.code} InputProps={{ readOnly: true }} helperText="Auto-generated" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Brand Name" value={formData.name} onChange={(e) => set('name', e.target.value)} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Website" value={formData.website} onChange={(e) => set('website', e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" multiline rows={2} value={formData.description} onChange={(e) => set('description', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Brand'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddCategory = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({ name: '', code: '', description: '', parentId: '', isActive: true });
  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  React.useEffect(() => {
    if (!open) return;
    Promise.all([
      itemCategoriesAPI.peek(),
      itemCategoriesAPI.getAll()
    ]).then(([codeRes, catsRes]) => {
      setFormData(prev => ({ ...prev, code: codeRes.data?.nextNumber || '' }));
      const data = catsRes.data?.categories || catsRes.data?.data?.categories || catsRes.data?.data || [];
      setCategories(Array.isArray(data) ? data : []);
    }).catch(() => { });
  }, [open]);

  const handleSave = async () => {
    if (!formData.name) { enqueueSnackbar('Name is required', { variant: 'error' }); return; }
    setSaving(true);
    try {
      const payload = { ...formData, parentId: formData.parentId || null };
      const response = await itemCategoriesAPI.create(payload);
      enqueueSnackbar('Category created successfully', { variant: 'success' });
      onSuccess(response.data?.data || response.data);
      onClose();
      setFormData({ name: '', code: '', description: '', parentId: '', isActive: true });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to create category', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Category</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Category Code" value={formData.code} InputProps={{ readOnly: true }} helperText="Auto-generated" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Category Name" value={formData.name} onChange={(e) => set('name', e.target.value)} required />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Parent Category (Optional)</InputLabel>
              <Select value={formData.parentId} onChange={(e) => set('parentId', e.target.value)} label="Parent Category (Optional)">
                <MenuItem value=""><em>None (Root Category)</em></MenuItem>
                {categories.filter(c => !c.parentId).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" multiline rows={2} value={formData.description} onChange={(e) => set('description', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddExpenseCategory = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', ledgerAccountId: '', code: '' });
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setFormData({ name: '', description: '', ledgerAccountId: '', code: '' });
    setAccountsLoading(true);

    // Fetch accounts and next code in parallel
    Promise.all([
      chartOfAccountsAPI.getAll({ type: 'expense' }).catch(() => ({ data: [] })),
      numberingAPI.peek('expense_category').catch(() => ({ data: { nextNumber: '' } }))
    ]).then(([accountsRes, codeRes]) => {
      const data = accountsRes.data;
      setAccounts(Array.isArray(data) ? data : (data?.data?.accounts || data?.accounts || []));
      if (codeRes.data?.nextNumber) {
        setFormData(prev => ({ ...prev, code: codeRes.data.nextNumber }));
      }
    }).finally(() => setAccountsLoading(false));
  }, [open]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Category name is required', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        ledgerAccountId: formData.ledgerAccountId || null,
        isActive: true
      };
      const res = await expenseCategoriesAPI.create(payload);
      const created = res.data?.data || res.data;
      queryClient.invalidateQueries(['expenseCategories']);
      queryClient.invalidateQueries(['expenseCategoriesList']);
      enqueueSnackbar(`Category "${created.name}" created`, { variant: 'success' });
      if (onSuccess) onSuccess(created);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to create category';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Expense Category</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              disabled
              label="Category Code"
              value={formData.code || 'Auto-generated'}
              helperText="Auto generated sequential code"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Category Name"
              value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              autoFocus
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description (optional)"
              value={formData.description}
              onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={accounts}
              loading={accountsLoading}
              getOptionLabel={o => o ? `${o.code ? o.code + ' - ' : ''}${o.name}` : ''}
              value={accounts.find(a => a.id === formData.ledgerAccountId) || null}
              onChange={(e, v) => setFormData(p => ({ ...p, ledgerAccountId: v?.id || '' }))}
              renderInput={params => (
                <TextField
                  {...params}
                  label="GL Account (Subledger)"
                  helperText="Link to an expense account in your chart of accounts"
                  InputProps={{ ...params.InputProps, endAdornment: <>{accountsLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddFixedAssetCategory = ({ open, onClose, onSuccess, initialData = null }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    depreciationMethod: 'straight_line',
    trackingMode: 'INDIVIDUAL',
    usefulLifeDefault: 60,
    capitalizationThreshold: 0
  });

  const DEPRECIATION_OPTIONS = [
    { value: 'straight_line', label: 'Straight Line' },
    { value: 'double_declining_balance', label: 'Double Declining Balance' },
    { value: 'none', label: 'No Depreciation' }
  ];

  const TRACKING_MODE_OPTIONS = [
    { value: 'INDIVIDUAL', label: 'Individual (One asset per unit)' },
    { value: 'BULK', label: 'Bulk (Grouped asset)' }
  ];

  React.useEffect(() => {
    if (!open) return;
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        code: initialData.code || '',
        description: initialData.description || '',
        depreciationMethod: initialData.depreciationMethod || 'straight_line',
        trackingMode: initialData.trackingMode || 'INDIVIDUAL',
        usefulLifeDefault: initialData.usefulLifeDefault || 60,
        capitalizationThreshold: initialData.capitalizationThreshold || 0
      });
    } else {
      setFormData({
        name: '',
        code: '',
        description: '',
        depreciationMethod: 'straight_line',
        trackingMode: 'INDIVIDUAL',
        usefulLifeDefault: 60,
        capitalizationThreshold: 0
      });
    }
  }, [open, initialData]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Category name is required', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim() || undefined,
        description: formData.description.trim() || undefined,
        depreciationMethod: formData.depreciationMethod || 'straight_line',
        trackingMode: formData.trackingMode || 'INDIVIDUAL',
        usefulLifeDefault: parseInt(formData.usefulLifeDefault, 10) || 60,
        capitalizationThreshold: parseFloat(formData.capitalizationThreshold) || 0,
        isActive: true
      };

      let result;
      if (initialData?.id) {
        const res = await fixedAssetsAPI.updateCategory(initialData.id, payload);
        result = res.data?.data || res.data;
        enqueueSnackbar(`Category "${result.name || formData.name}" updated`, { variant: 'success' });
      } else {
        const res = await fixedAssetsAPI.createCategory(payload);
        result = res.data?.data || res.data;
        enqueueSnackbar(`Category "${result.name || formData.name}" created`, { variant: 'success' });
      }

      queryClient.invalidateQueries(['fixed-asset-categories']);
      queryClient.invalidateQueries(['assetCategories']);
      if (onSuccess) onSuccess(result);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to save category';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const isEdit = Boolean(initialData?.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Asset Category' : 'Add New Asset Category'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              required
              label="Category Name"
              value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Category Code"
              placeholder="Auto-generated if empty"
              value={formData.code}
              onChange={e => setFormData(p => ({ ...p, code: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={DEPRECIATION_OPTIONS}
              getOptionLabel={option => option.label || option.value || ''}
              value={DEPRECIATION_OPTIONS.find(o => o.value === formData.depreciationMethod) || DEPRECIATION_OPTIONS[0]}
              onChange={(e, val) => setFormData(p => ({ ...p, depreciationMethod: val ? val.value : 'straight_line' }))}
              renderInput={params => <TextField {...params} label="Depreciation Method" fullWidth />}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={TRACKING_MODE_OPTIONS}
              getOptionLabel={option => option.label || option.value || ''}
              value={TRACKING_MODE_OPTIONS.find(o => o.value === formData.trackingMode) || TRACKING_MODE_OPTIONS[0]}
              onChange={(e, val) => setFormData(p => ({ ...p, trackingMode: val ? val.value : 'INDIVIDUAL' }))}
              renderInput={params => <TextField {...params} label="Tracking Mode" fullWidth />}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Default Useful Life (Months)"
              type="number"
              value={formData.usefulLifeDefault}
              onChange={e => setFormData(p => ({ ...p, usefulLifeDefault: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Capitalization Threshold"
              type="number"
              value={formData.capitalizationThreshold}
              onChange={e => setFormData(p => ({ ...p, capitalizationThreshold: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description (optional)"
              value={formData.description}
              onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : (isEdit ? 'Update Category' : 'Create Category')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddWarehouse = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', location: '' });

  React.useEffect(() => {
    if (!open) return;
    setFormData({ name: '', location: '' });
  }, [open]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Warehouse name is required', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        location: formData.location.trim() || undefined,
        isActive: true,
        backorderPolicy: 'NEVER'
      };
      const res = await warehouseAPI.create(payload);
      const created = res.data?.warehouse || res.data?.data || res.data;
      queryClient.invalidateQueries(['warehouses']);
      enqueueSnackbar(`Warehouse "${created.name}" created successfully`, { variant: 'success' });
      if (onSuccess) onSuccess(created);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to create warehouse';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Warehouse / Location</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Warehouse / Location Name"
              value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              autoFocus
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address / Location Details (optional)"
              value={formData.location}
              onChange={e => setFormData(p => ({ ...p, location: e.target.value }))}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Warehouse'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddProject = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    budget: '',
    status: 'active',
    startDate: '',
    endDate: ''
  });

  React.useEffect(() => {
    if (!open) return;
    setFormData({
      name: '',
      code: '',
      budget: '',
      status: 'active',
      startDate: new Date().toISOString().split('T')[0],
      endDate: ''
    });
  }, [open]);

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      enqueueSnackbar('Project name is required', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code?.trim() || undefined,
        budget: formData.budget !== '' ? parseFloat(formData.budget) : 0,
        status: formData.status || 'active',
        startDate: formData.startDate || null,
        endDate: formData.endDate || null
      };
      const res = await projectsAPI.create(payload);
      const created = res.data?.data || res.data?.project || res.data;
      queryClient.invalidateQueries(['projects']);
      enqueueSnackbar(`Project "${created.name || formData.name}" created successfully`, { variant: 'success' });
      if (onSuccess) onSuccess(created);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to create project';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Project</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              required
              label="Project Name"
              value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Project Code"
              placeholder="e.g. PRJ-001"
              value={formData.code}
              onChange={e => setFormData(p => ({ ...p, code: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Budget"
              type="number"
              value={formData.budget}
              onChange={e => setFormData(p => ({ ...p, budget: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={e => setFormData(p => ({ ...p, status: e.target.value }))}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="planning">Planning</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.startDate}
              onChange={e => setFormData(p => ({ ...p, startDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Expected End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.endDate}
              onChange={e => setFormData(p => ({ ...p, endDate: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const QuickAddBranch = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    country: '',
    description: ''
  });

  React.useEffect(() => {
    if (!open) return;
    setFormData({ code: '', name: '', country: '', description: '' });
  }, [open]);

  const handleSave = async () => {
    if (!formData.code?.trim() || !formData.name?.trim()) {
      enqueueSnackbar('Branch Code and Name are required', { variant: 'error' });
      return;
    }
    const branchCode = formData.code.trim().toUpperCase();
    const branchName = formData.name.trim();
    const tenantKey = user?.tenantId || 'global';
    const storageKey = `fluxerp_custom_branches_${tenantKey}`;
    setSaving(true);
    try {
      const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const filtered = existing.filter(b => b.code !== branchCode);
      const newBranch = {
        code: branchCode,
        name: branchName,
        country: formData.country?.trim() || '',
        description: formData.description?.trim() || ''
      };
      filtered.push(newBranch);
      localStorage.setItem(storageKey, JSON.stringify(filtered));
      enqueueSnackbar(`Branch "${branchName} (${branchCode})" added successfully`, { variant: 'success' });
      if (onSuccess) onSuccess(newBranch);
      onClose();
    } catch (err) {
      enqueueSnackbar('Failed to add branch', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Branch / Region</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Branch Code"
              placeholder="e.g. DXB, LHR"
              value={formData.code}
              onChange={e => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
              autoFocus
              helperText="2-10 chars (uppercase)"
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              required
              label="Branch / Region Name"
              placeholder="e.g. Dubai Regional Office"
              value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Country / Territory (optional)"
              placeholder="e.g. United Arab Emirates"
              value={formData.country}
              onChange={e => setFormData(p => ({ ...p, country: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description / Location Details (optional)"
              value={formData.description}
              onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Adding...' : 'Add Branch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


